<script lang="ts" setup>
  import MainNavDesktop from '@/components/landingPage/nav/MainNavDesktop.vue'
  import MainNavMobile from '@/components/landingPage/nav/MainNavMobile.vue'
  import type { JwtContent } from '@/types'
  import { onMounted, reactive, ref, watch } from 'vue'
  import { useCartStore, useJwtStore } from '@/stores'
  import { JwtParser, useTypedRoute } from '@/composables'
  import { useRouter } from 'vue-router'
  import { STATUS_CODES } from '@/constants'
  import { useAxios } from '@/composables/useAxios'
  import { handleAxiosError } from '@/composables/useAxiosError'
  import { useIsMobile } from '@/composables/useIsMobile'
  import { clearAllUrlTokens } from '@/utils/urlToken'

  const route = useTypedRoute<{ id: string }>()
  const router = useRouter()

  const cartStore = useCartStore()
  const jwtStore = useJwtStore()

  const value = ref<string | null>(null)
  const { isMobile } = useIsMobile(700)

  const data = reactive({
    jwt: {} as JwtContent,
    loggedIn: false,
    showNav: false
  })

  const setJwt = () => {
    const localJwt = jwtStore.getJwt
    if (localJwt !== null && jwtStore.isJwtValid(localJwt)) {
      const parsed = JwtParser(localJwt)
      if (!parsed) return
      data.jwt = parsed
      data.loggedIn = true
    } else {
      // Token is null or expired - clean up
      if (localJwt !== null) {
        jwtStore.removeJwt()
      }
      data.loggedIn = false
    }
  }

  const getItemsCount = (): number => cartStore.cartItemCount

  const handleLogout = () => {
    // Here rather than in removeJwt, which also runs for an expired session
    // while an emailed link is being followed.
    clearAllUrlTokens()
    // authMiddleware.JWTAuthRequired, authMiddleware.JWTLogout
    // 200, 400, 401, 404, 500
    useAxios<null, null>('HEAD', '/account/logout', true)
      .then((res) => {
        if (!res) return
        if (res.status === STATUS_CODES.OK) {
          // Only clear auth-related items, not all localStorage
          jwtStore.removeJwt()
          data.loggedIn = false
        }
      })
      .catch((error) => {
        handleAxiosError(error, { logPrefix: 'MainNav:Logout' })
      })
      .finally(() => {
        setTimeout(() => {
          if (route.fullPath !== '/') {
            router.replace({ name: 'landingPage' })
          } else {
            window.location.reload()
          }
        }, 500)
      })
  }

  watch(
    () => route.name,
    () => {
      // Switch on the route NAME, not the matched path. Public routes now sit
      // under /:locale(de|en), so matched[1].path is '/:locale(de|en)/products'
      // and every literal case below stopped matching, silently killing the
      // highlight on all public pages.
      const name = typeof route.name === 'string' ? route.name : ''
      switch (name) {
        case 'landingPage':
          value.value = 'home'
          break
        case 'products':
        case 'productsHome':
        case 'kubernetes':
        case 'vserver':
        case 'webhosting':
        case 'domains':
        case 'domainsSearch':
        case 'domainsTlds':
        case 'objectStorage':
        case 'emailHosting':
        case 'pricingCalculator':
          value.value = 'products'
          break
        case 'cart':
          value.value = 'cart'
          break
        case 'login':
          value.value = 'login'
          break
        case 'dashboard':
          value.value = 'dashboard'
          break
        default:
          value.value = null
          break
      }
    }
  )

  watch(
    () => jwtStore.$state.state,
    () => {
      // Always call setJwt when JWT state changes (including when removed)
      setJwt()
    }
  )

  onMounted(() => {
    if (jwtStore.getJwt) {
      setJwt()
    }
  })
</script>

<template>
  <main-nav-desktop
    v-if="!isMobile"
    :cart-items="getItemsCount()"
    :jwt="data.jwt"
    :logged-in="data.loggedIn"
    @logout="handleLogout"
  />
  <main-nav-mobile
    v-else
    v-model="value"
    :cart-items="getItemsCount()"
    :jwt="data.jwt"
    :logged-in="data.loggedIn"
    @logout="handleLogout"
  />
</template>

<style lang="scss" scoped>
  /* Scoped styles remain here */
</style>

<style lang="scss">
  /* Glass effect for mobile bottom navigation to match desktop */
  .v-bottom-navigation {
    background: rgba(10, 12, 16, 0.6) !important;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
</style>
